/**
 * CertUploadModal.
 *
 * Modal para cargar/renovar certificado ECD BYO desde Configuración.
 * Antes el upload de cert solo estaba accesible en el wizard onboarding,
 * lo que dejaba al cliente sin forma de renovar cuando vencía. Este
 * componente desbloquea ese flow self-service.
 *
 * Maneja:
 *   - File input restringido a .p12, .pfx (PKCS#12).
 *   - Password requerida.
 *   - NIT esperado (opcional, validación adicional backend-side).
 *   - Idempotencia: si ya existe cert, lo reemplaza atómicamente
 *     (backend archive + insert).
 */

import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, ShieldCheck, Upload, X } from 'lucide-react'
import { toast } from 'sonner'

import { certApi } from '@/lib/api'

interface Props {
  taxClientID: string
  // expectedNIT opcional — si se pasa, backend valida que el Subject
  // del cert contenga este NIT (defensa contra cargar el cert de otra
  // empresa por error).
  expectedNIT?: string
  isRenewal?: boolean
  onClose: () => void
  onSuccess?: () => void
}

export function CertUploadModal({
  taxClientID,
  expectedNIT,
  isRenewal,
  onClose,
  onSuccess,
}: Props) {
  const qc = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [password, setPassword] = useState('')
  const [showPwd, setShowPwd] = useState(false)
  const [confirmReplace, setConfirmReplace] = useState(!isRenewal)

  const upload = useMutation({
    mutationFn: () => {
      if (!file) throw new Error('Adjunta el archivo .p12 / .pfx')
      return certApi.upload(taxClientID, file, password, expectedNIT)
    },
    onSuccess: () => {
      toast.success(isRenewal ? 'Certificado renovado' : 'Certificado cargado')
      qc.invalidateQueries({ queryKey: ['settings-cert', taxClientID] })
      qc.invalidateQueries({ queryKey: ['settings-cert'] })
      onSuccess?.()
      onClose()
    },
    onError: (e: unknown) => {
      const err = e as {
        response?: { data?: { detail?: { message?: string; code?: string } } }
      }
      const msg = err.response?.data?.detail?.message ?? (e as Error).message
      toast.error(`Error: ${msg}`)
    },
  })

  // Password may be empty for self-issued or test PKCS#12 files. The
  // backend handles both cases. The "noPassword" checkbox makes the
  // empty case an explicit decision so the user does not submit by
  // mistake after losing focus on the input.
  const [noPassword, setNoPassword] = useState(false)
  const canUpload =
    file !== null &&
    (password.length > 0 || noPassword) &&
    confirmReplace &&
    !upload.isPending

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-xl bg-bg-surface p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-brand-600" />
            <h2 className="text-lg font-semibold">
              {isRenewal ? 'Renovar certificado ECD' : 'Cargar certificado ECD'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-fg-muted hover:bg-bg-elevated hover:text-fg-primary"
            aria-label="Cerrar"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {isRenewal && (
          <div className="mt-4 rounded-md border border-yellow-300 bg-yellow-50 p-3 text-xs text-yellow-900 dark:bg-yellow-950 dark:text-yellow-100 dark:border-yellow-800">
            <strong>Renovación:</strong> esta acción reemplaza tu certificado
            activo. Los documentos ya emitidos conservan su firma original
            (XML inmutable). Los próximos documentos se firmarán con el cert
            nuevo.
          </div>
        )}

        <div className="mt-5 space-y-4">
          <div>
            <label className="block text-xs font-medium uppercase tracking-wide text-fg-secondary">
              Archivo del certificado *
            </label>
            <input
              ref={fileRef}
              type="file"
              accept=".p12,.pfx,application/x-pkcs12"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="mt-1.5 flex w-full items-center gap-2 rounded-md border border-dashed border-border bg-bg-app px-3 py-3 text-sm text-fg-secondary hover:bg-bg-elevated"
            >
              <Upload className="h-4 w-4" />
              {file ? (
                <span className="font-mono text-xs">
                  {file.name} ({Math.round(file.size / 1024)} KB)
                </span>
              ) : (
                'Elegir archivo .p12 o .pfx'
              )}
            </button>
            <p className="mt-1 text-xs text-fg-muted">
              Formato PKCS#12 emitido por una CA autorizada DIAN (Andes SCD,
              Certicámara, GSE, Camerfirma, etc.).
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium uppercase tracking-wide text-fg-secondary">
              Contraseña del certificado {noPassword ? '' : '*'}
            </label>
            <div className="relative mt-1.5">
              <input
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={noPassword}
                autoComplete="off"
                className="w-full rounded-md border border-border bg-bg-app px-3 py-2.5 pr-16 text-sm text-fg-primary outline-none transition-colors focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <button
                type="button"
                onClick={() => setShowPwd((s) => !s)}
                disabled={noPassword}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded px-2 py-1 text-xs text-fg-muted hover:bg-bg-elevated disabled:opacity-30"
              >
                {showPwd ? 'Ocultar' : 'Ver'}
              </button>
            </div>
            <label className="mt-2 flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={noPassword}
                onChange={(e) => {
                  setNoPassword(e.target.checked)
                  if (e.target.checked) setPassword('')
                }}
              />
              <span>Este certificado no tiene contraseña</span>
            </label>
            <p className="mt-1 text-xs text-fg-muted">
              No la guardamos en texto: solo se usa para extraer la llave
              privada y se encripta con nuestra clave maestra antes de persistir.
            </p>
          </div>

          {expectedNIT && (
            <div className="rounded-md border border-info/30 bg-info/10 p-3 text-xs">
              <p>
                Verificaremos que el certificado contenga el NIT{' '}
                <code className="font-mono">{expectedNIT}</code> en el Subject.
                Si no, rechazamos.
              </p>
            </div>
          )}

          {isRenewal && (
            <div>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmReplace}
                  onChange={(e) => setConfirmReplace(e.target.checked)}
                  className="mt-1"
                />
                <span>
                  Entiendo que esta acción reemplaza mi certificado activo y
                  los próximos documentos se firmarán con el nuevo.
                </span>
              </label>
            </div>
          )}
        </div>

        <footer className="mt-6 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border bg-bg-app px-4 py-2 text-sm font-medium hover:bg-bg-elevated"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => upload.mutate()}
            disabled={!canUpload}
            className="inline-flex items-center gap-2 rounded-md bg-gradient-to-r from-brand-500 to-rose-500 px-4 py-2 text-sm font-medium text-white transition-all hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
          >
            {upload.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Subiendo…
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                {isRenewal ? 'Renovar certificado' : 'Cargar certificado'}
              </>
            )}
          </button>
        </footer>
      </div>
    </div>
  )
}
